import React from "react";
import { motion } from "framer-motion";
import {
  User, MapPin, Building2, Calendar, Star, BookOpen,
  HelpCircle, Loader2, Brain, TrendingUp,
  Clock, Zap, CheckCircle,
  Heart, Trophy, XCircle, Sparkles, FileText, Shield,
  Target, GitBranch, Database, MessageSquare
} from "lucide-react";
import useLocalization from "../../../../hooks/useLocalization";
import PropTypes from "prop-types";


const NeuralNetwork = ({ size = "large" }) => {
  const nodeCount = size === "large" ? 12 : 8;
  const nodes = Array.from({ length: nodeCount }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 2
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
      <svg className="w-full h-full" viewBox="0 0 100 100">
        {nodes.map((node, i) =>
          nodes.slice(i + 1).map((otherNode, j) => (
            <motion.line
              key={`${i}-${j}`}
              x1={node.x}
              y1={node.y}
              x2={otherNode.x}
              y2={otherNode.y}
              stroke="currentColor"
              strokeWidth="0.2"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{
                pathLength: [0, 1, 0],
                opacity: [0, 0.6, 0]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: node.delay + j * 0.1
              }}
            />
          ))
        )}

        {nodes.map((node, i) => (
          <motion.circle
            key={i}
            cx={node.x}
            cy={node.y}
            r="1"
            fill="currentColor"
            initial={{ scale: 0 }}
            animate={{
              scale: [0.5, 1.5, 0.5],
              opacity: [0.3, 1, 0.3]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: node.delay
            }}
          />
        ))}
      </svg>
    </div>
  );
};

NeuralNetwork.propTypes = {
  size: PropTypes.string,
};



const DataFlow = ({ direction = "horizontal" }) => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-blue-400 rounded-full"
          style={{
            left: direction === "horizontal" ? "-8px" : `${20 + i * 15}%`,
            top: direction === "horizontal" ? `${20 + i * 15}%` : "-8px"
          }}
          animate={direction === "horizontal"
            ? { x: [0, 200], opacity: [0, 1, 0] }
            : { y: [0, 200], opacity: [0, 1, 0] }
          }
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.4,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

DataFlow.propTypes = {
  direction: PropTypes.string,
};


const PulsingOrb = ({ color = "blue", size = "lg" }) => {
  const sizeClasses = {
    sm: "w-16 h-16",
    md: "w-20 h-20",
    lg: "w-24 h-24",
    xl: "w-32 h-32"
  };

  const colorMappings = {
    blue: {
      border: "border-blue-400",
      gradient: "from-blue-400 to-blue-600",
      shadow: "0 0 20px rgba(59, 130, 246, 0.5)"
    },
    purple: {
      border: "border-purple-400",
      gradient: "from-purple-400 to-purple-600",
      shadow: "0 0 20px rgba(147, 51, 234, 0.5)"
    },
    green: {
      border: "border-green-400",
      gradient: "from-green-400 to-green-600",
      shadow: "0 0 20px rgba(34, 197, 94, 0.5)"
    },
    indigo: {
      border: "border-indigo-400",
      gradient: "from-indigo-400 to-indigo-600",
      shadow: "0 0 20px rgba(99, 102, 241, 0.5)"
    },
    yellow: {
      border: "border-yellow-400",
      gradient: "from-yellow-400 to-yellow-600",
      shadow: "0 0 20px rgba(234, 179, 8, 0.5)"
    },
    orange: {
      border: "border-orange-400",
      gradient: "from-orange-400 to-orange-600",
      shadow: "0 0 20px rgba(249, 115, 22, 0.5)"
    },
    cyan: {
      border: "border-cyan-400",
      gradient: "from-cyan-400 to-cyan-600",
      shadow: "0 0 20px rgba(6, 182, 212, 0.5)"
    },
    pink: {
      border: "border-pink-400",
      gradient: "from-pink-400 to-pink-600",
      shadow: "0 0 20px rgba(236, 72, 153, 0.5)"
    },
    red: {
      border: "border-red-400",
      gradient: "from-red-400 to-red-600",
      shadow: "0 0 20px rgba(239, 68, 68, 0.5)"
    },
    emerald: {
      border: "border-emerald-400",
      gradient: "from-emerald-400 to-emerald-600",
      shadow: "0 0 20px rgba(16, 185, 129, 0.5)"
    },
    rose: {
      border: "border-rose-400",
      gradient: "from-rose-400 to-rose-600",
      shadow: "0 0 20px rgba(244, 63, 94, 0.5)"
    },
    amber: {
      border: "border-amber-400",
      gradient: "from-amber-400 to-amber-600",
      shadow: "0 0 20px rgba(245, 158, 11, 0.5)"
    }
  };

  const colorConfig = colorMappings[color] || colorMappings.blue;

  return (
    <div className="relative flex items-center justify-center">
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full border-2 ${colorConfig.border}`}
          style={{
            width: `${100 + i * 20}%`,
            height: `${100 + i * 20}%`
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.6, 0.2, 0.6]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.3
          }}
        />
      ))}

      <motion.div
        className={`${sizeClasses[size]} rounded-full bg-gradient-to-br ${colorConfig.gradient} shadow-lg flex items-center justify-center relative overflow-hidden`}
        animate={{
          boxShadow: [
            colorConfig.shadow,
            colorConfig.shadow.replace('0.5', '0.8'),
            colorConfig.shadow
          ]
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <NeuralNetwork size="small" />
      </motion.div>
    </div>
  );
};

PulsingOrb.propTypes = {
  color: PropTypes.string,
  size: PropTypes.string,
};

const getNerAnimations = (t) => (
  {
    "B-PER": {
      label: t("ner.person"),
      icon: <User className="w-6 h-6" />,
      gradient: "from-blue-400 to-blue-600",
      textColor: "text-blue-700 dark:text-blue-300",
    },
    "B-LOC": {
      label: t("ner.location"),
      icon: <MapPin className="w-6 h-6" />,
      gradient: "from-green-400 to-green-600",
      textColor: "text-green-700 dark:text-green-300",
    },
    "B-ORG": {
      label: t("ner.organization"),
      icon: <Building2 className="w-6 h-6" />,
      gradient: "from-yellow-500 to-yellow-600",
      textColor: "text-yellow-700 dark:text-yellow-300",
    },
    "B-DATE": {
      label: t("ner.date"),
      icon: <Calendar className="w-6 h-6" />,
      gradient: "from-amber-400 to-amber-600",
      textColor: "text-amber-700 dark:text-amber-300",
    },
    "B-MISC": {
      label: t("ner.misc"),
      icon: <Star className="w-6 h-6" />,
      gradient: "from-pink-400 to-pink-600",
      textColor: "text-pink-700 dark:text-pink-300",
    },
    "B-EVENT": {
      label: t("ner.event"),
      icon: <BookOpen className="w-6 h-6" />,
      gradient: "from-indigo-400 to-indigo-600",
      textColor: "text-indigo-700 dark:text-indigo-300",
    },
  }
);

const getApiProgressAnimations = (t) => ({
  INITIATING: {
    icon: <Loader2 className="w-8 h-8 animate-spin" />,
    color: "blue",
    label: t("apiProgress.initiating"),
    bgPattern: "dots",
    textColor: "text-blue-700 dark:text-blue-300",
  },
  PROCESSING: {
    icon: <FileText className="w-8 h-8" />,
    color: "blue",
    label: t("apiProgress.processing"),
    bgPattern: "dots",
    textColor: "text-blue-700 dark:text-blue-300",
  },
  WORKING: {
    icon: <Brain className="w-8 h-8" />,
    color: "indigo",
    label: t("apiProgress.working"),
    bgPattern: "neural",
    textColor: "text-indigo-700 dark:text-indigo-300",
  },
  PROGRESS: {
    icon: <TrendingUp className="w-8 h-8" />,
    color: "green",
    label: t("apiProgress.progress"),
    bgPattern: "flow",
    textColor: "text-green-700 dark:text-green-300",
  },
  DELAY: {
    icon: <Clock className="w-8 h-8" />,
    color: "yellow",
    label: t("apiProgress.delay"),
    bgPattern: "pulse",
    textColor: "text-yellow-700 dark:text-yellow-300",
  },
  PATIENCE: {
    icon: <Shield className="w-8 h-8" />,
    color: "orange",
    label: t("apiProgress.patience"),
    bgPattern: "neural",
    textColor: "text-orange-700 dark:text-orange-300",
  },
  COMPLEX: {
    icon: <GitBranch className="w-8 h-8" />,
    color: "purple",
    label: t("apiProgress.complex"),
    bgPattern: "neural",
    textColor: "text-purple-700 dark:text-purple-300",
  },
  PERSISTENT: {
    icon: <Database className="w-8 h-8" />,
    color: "cyan",
    label: t("apiProgress.persistent"),
    bgPattern: "flow",
    textColor: "text-cyan-700 dark:text-cyan-300",
  },
  OPTIMIZING: {
    icon: <Target className="w-8 h-8" />,
    color: "pink",
    label: t("apiProgress.optimizing"),
    bgPattern: "pulse",
    textColor: "text-pink-700 dark:text-pink-300",
  },
  INTENSIVE: {
    icon: <Zap className="w-8 h-8" />,
    color: "red",
    label: t("apiProgress.intensive"),
    bgPattern: "neural",
    textColor: "text-red-700 dark:text-red-300",
  },
  FINALIZING: {
    icon: <CheckCircle className="w-8 h-8" />,
    color: "emerald",
    label: t("apiProgress.finalizing"),
    bgPattern: "success",
    textColor: "text-emerald-700 dark:text-emerald-300",
  },
  APPRECIATION: {
    icon: <Heart className="w-8 h-8" />,
    color: "rose",
    label: t("apiProgress.appreciation"),
    bgPattern: "celebration",
    textColor: "text-rose-700 dark:text-rose-300",
  },
  COMPLETION: {
    icon: <Trophy className="w-8 h-8" />,
    color: "amber",
    label: t("apiProgress.completion"),
    bgPattern: "celebration",
    textColor: "text-amber-700 dark:text-amber-300",
  },
  SUCCESS: {
    icon: <CheckCircle className="w-8 h-8" />,
    color: "green",
    label: t("apiProgress.success"),
    bgPattern: "success",
    textColor: "text-green-700 dark:text-green-300",
  },
  ERROR: {
    icon: <XCircle className="w-8 h-8" />,
    color: "red",
    label: t("apiProgress.error"),
    bgPattern: "error",
    textColor: "text-red-700 dark:text-red-300",
  },
});



const EnhancedProgressBar = ({ percentage, type, step, totalSteps }) => {
  const { t } = useLocalization();
  const apiProgressAnimations = getApiProgressAnimations(t);
  const config = apiProgressAnimations[type] || apiProgressAnimations.PROCESSING;

  const progressColorMappings = {
    blue: "from-blue-400 to-blue-600",
    indigo: "from-indigo-400 to-indigo-600",
    green: "from-green-400 to-green-600",
    yellow: "from-yellow-400 to-yellow-600",
    orange: "from-orange-400 to-orange-600",
    purple: "from-purple-400 to-purple-600",
    cyan: "from-cyan-400 to-cyan-600",
    pink: "from-pink-400 to-pink-600",
    red: "from-red-400 to-red-600",
    emerald: "from-emerald-400 to-emerald-600",
    rose: "from-rose-400 to-rose-600",
    amber: "from-amber-400 to-amber-600"
  };

  const progressGradient = progressColorMappings[config.color] || progressColorMappings.blue;

  return (
    <div className="w-full max-w-md mx-auto mb-6">
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
          {step && totalSteps ? `Step ${step} of ${totalSteps}` : 'Processing...'}
        </span>
        <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
          {percentage}%
        </span>
      </div>

      <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <motion.div
          className={`h-full bg-gradient-to-r ${progressGradient} rounded-full relative overflow-hidden`}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{ x: [-100, 200] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
        </motion.div>
      </div>
    </div>
  );
};


EnhancedProgressBar.propTypes = {
  percentage: PropTypes.number.isRequired,
  type: PropTypes.string,
  step: PropTypes.number,
  totalSteps: PropTypes.number,
};



const CelebrationSparkles = ({ color }) => {
  const sparkleColorMappings = {
    amber: "text-amber-400",
    green: "text-green-400",
    rose: "text-rose-400",
    blue: "text-blue-400",
    purple: "text-purple-400"
  };

  const sparkleColor = sparkleColorMappings[color] || sparkleColorMappings.blue;

  return (
    <div className="absolute inset-0 pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`
          }}
          animate={{
            y: [-10, -100],
            opacity: [1, 0],
            rotate: [0, 360]
          }}
          transition={{
            duration: 2,
            delay: i * 0.1,
            repeat: Infinity
          }}
        >
          <Sparkles className={`w-4 h-4 ${sparkleColor}`} />
        </motion.div>
      ))}
    </div>
  );
};
CelebrationSparkles.propTypes = {
  color: PropTypes.string,
};
const deduplicateNerData = (nerContent) => {
  if (!nerContent || !Array.isArray(nerContent)) return [];

  const nerMap = new Map();

  nerContent.forEach(item => {
    if (item && item.token && item.tag) {
      // Use token + tag as the key for uniqueness
      const key = `${item.token.toLowerCase()}-${item.tag}`;
      if (!nerMap.has(key)) {
        nerMap.set(key, item);
      }
    }
  });

  return Array.from(nerMap.values());
};

const NerLoadingState = () => {
  const { t } = useLocalization();
  return (
    <motion.div
      className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl border border-purple-200 dark:border-purple-700 mb-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="relative">
        <PulsingOrb color="purple" size="md" />
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        >
          <MessageSquare className="w-6 h-6 text-white" />
        </motion.div>
      </div>

      <motion.div
        className="mt-4 text-center"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h3 className="text-lg font-semibold text-purple-700 dark:text-purple-300 mb-1">
          {t('analyzingGrievance')}
        </h3>
        <p className="text-sm text-purple-600 dark:text-purple-400">
          {t('extractingKeyInformation')}
        </p>
      </motion.div>
    </motion.div>
  );
};


const NerResults = ({ nerContent }) => {
  const { t } = useLocalization();
  const nerAnimations = getNerAnimations(t);
  const deduplicatedNer = deduplicateNerData(nerContent);

  if (deduplicatedNer.length === 0) return null;

  return (
    <motion.div
      className="mb-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* <motion.h3
        className="text-lg font-semibold text-center mb-4 text-gray-800 dark:text-gray-200 flex items-center justify-center gap-2"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <CheckCircle className="w-5 h-5 text-green-500" />
        Identified Information ({deduplicatedNer.length})
      </motion.h3> */}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {deduplicatedNer.map((item, idx) => {
          const config = nerAnimations[item.tag] || {
            label: item.tag || "Unknown",
            icon: <HelpCircle className="w-6 h-6" />,
            gradient: "from-gray-400 to-gray-600",
            textColor: "text-gray-700 dark:text-gray-300"
          };

          return (
            <motion.div
              key={`${item.token}-${item.tag}-${idx}`}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
              className="relative overflow-hidden"
            >
              <motion.div
                className={`p-3 rounded-lg bg-gradient-to-br ${config.gradient} shadow-md border border-white/20 backdrop-blur-sm`}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-3 text-white">
                  <motion.div
                    whileHover={{ rotate: 15 }}
                    transition={{ duration: 0.2 }}
                  >
                    {config.icon}
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate text-sm">{item.token}</p>
                    <p className="text-xs opacity-90">{config.label}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

NerResults.propTypes = {
  nerContent: PropTypes.array,
};





const ApiStatusSection = ({ progressData, isApiResponding }) => {
  const { t } = useLocalization();
  if (!isApiResponding && !progressData) return null;
  const apiProgressAnimations = getApiProgressAnimations(t);

  const { message, type, percentage = 0, step, totalSteps } = progressData || {};
  const config = apiProgressAnimations[type] || apiProgressAnimations.PROCESSING;

  return (
    <motion.div
      className="flex flex-col items-center justify-center p-6 relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {config.bgPattern === "neural" && <NeuralNetwork />}
      {config.bgPattern === "flow" && <DataFlow />}

      {percentage > 0 && (
        <EnhancedProgressBar
          percentage={percentage}
          type={type}
          step={step}
          totalSteps={totalSteps}
        />
      )}

      <div className="relative z-10">
        <PulsingOrb color={config.color} size="md" />

        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{
            rotate: type === "COMPLEX" ? 360 : 0,
            scale: [1, 1.1, 1]
          }}
          transition={{
            rotate: { duration: 4, repeat: Infinity, ease: "linear" },
            scale: { duration: 2, repeat: Infinity }
          }}
        >
          <div className="text-white">
            {config.icon}
          </div>
        </motion.div>
      </div>

      <motion.div
        className="mt-6 text-center max-w-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <motion.h3
          className={`text-lg font-bold ${config.textColor}`}
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {config.label}
        </motion.h3>

        <motion.p
          className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
          animate={{ opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          {message || "Processing your request..."}
        </motion.p>

        {(type === "COMPLETION" || type === "SUCCESS") && (
          <CelebrationSparkles color={config.color} />
        )}
      </motion.div>
    </motion.div>
  );
};

ApiStatusSection.propTypes = {
  progressData: PropTypes.object,
  isApiResponding: PropTypes.bool,
};



const DefaultThinkingState = ({ t }) => {
  return (
    <motion.div
      className="flex flex-col items-center justify-center min-h-[200px] p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="relative">
        <PulsingOrb color="blue" size="md" />
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        >
          <Brain className="w-8 h-8 text-white" />
        </motion.div>
      </div>

      <motion.div
        className="mt-6 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-2">
          {t("userEngageMsg.thinking") || "AI Agent Thinking..."}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('preparingToAssist')}
        </p>
      </motion.div>
    </motion.div>
  );
};

DefaultThinkingState.propTypes = {
  t: PropTypes.func.isRequired,
};


export default function StatusAnimator({ progressData, isApiResponding, nerContent, isNerResponding }) {
  const { t } = useLocalization();

  const hasNerResults = nerContent && Array.isArray(nerContent) && nerContent.length > 0;
  const hasApiActivity = isApiResponding || progressData;
  const showDefault = !isNerResponding && !hasNerResults && !hasApiActivity;
  // console.log(`isNerResponding: ${isNerResponding}, hasNerResults: ${hasNerResults}, hasApiActivity: ${hasApiActivity}, showDefault: ${showDefault}`);

  return (
    <div className="space-y-4">
      {isNerResponding && <NerLoadingState />}
      {hasNerResults && <NerResults nerContent={nerContent} />}

      {hasApiActivity && (
        <ApiStatusSection
          progressData={progressData}
          isApiResponding={isApiResponding}
        />
      )}

      {showDefault && <DefaultThinkingState t={t} />}
    </div>
  );
}

StatusAnimator.propTypes = {
  progressData: PropTypes.object,
  isApiResponding: PropTypes.bool,
  nerContent: PropTypes.array,
  isNerResponding: PropTypes.bool,
};
